/**
 * Ladder Challenge Modal
 * Allows players to submit a challenge to someone ranked above them
 */

import { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc, addDoc, query, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { LadderEntry, LadderSettings } from '../types';

interface LadderChallengeModalProps {
    isOpen: boolean;
    onClose: () => void;
    playerId: string;          // playerRosterId
    playerName: string;
}

export default function LadderChallengeModal({
    isOpen,
    onClose,
    playerId,
    playerName,
}: LadderChallengeModalProps) {
    const [rankings, setRankings] = useState<LadderEntry[]>([]);
    const [settings, setSettings] = useState<LadderSettings | null>(null);
    const [myRank, setMyRank] = useState<LadderEntry | null>(null);
    const [eligibleOpponents, setEligibleOpponents] = useState<LadderEntry[]>([]);
    const [selectedOpponent, setSelectedOpponent] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) return;

        async function loadData() {
            setLoading(true);
            setError(null);
            try {
                // Load rankings
                const ladderQuery = query(collection(db, 'ssbuLadder'), orderBy('rank', 'asc'));
                const ladderSnap = await getDocs(ladderQuery);
                const ladderData = ladderSnap.docs.map(d => ({
                    id: d.id,
                    ...d.data()
                })) as LadderEntry[];
                setRankings(ladderData);

                // Load settings
                const settingsDoc = await getDoc(doc(db, 'settings', 'ssbuLadder'));
                if (settingsDoc.exists()) {
                    setSettings(settingsDoc.data() as LadderSettings);
                }

                // Find my rank
                const me = ladderData.find(e => e.playerRosterId === playerId);
                setMyRank(me || null);

                // Calculate eligible opponents
                if (me && settingsDoc.exists()) {
                    const settingsData = settingsDoc.data() as LadderSettings;
                    const topTierThreshold = settingsData.topTierThreshold || 5;
                    const topTierRange = settingsData.topTierChallengeRange || 1;
                    const defaultRange = settingsData.defaultChallengeRange || 3;

                    const isTopTier = me.rank <= topTierThreshold;
                    const challengeRange = isTopTier ? topTierRange : defaultRange;
                    const minRank = Math.max(1, me.rank - challengeRange);

                    const eligible = ladderData.filter(e =>
                        e.rank >= minRank && e.rank < me.rank
                    );
                    setEligibleOpponents(eligible);
                }
            } catch (err) {
                console.error('Error loading data:', err);
                setError('Failed to load ladder data');
            } finally {
                setLoading(false);
            }
        }

        loadData();
    }, [isOpen, playerId]);

    const handleSubmitChallenge = async () => {
        if (!selectedOpponent || !myRank) return;

        const opponent = rankings.find(r => r.id === selectedOpponent);
        if (!opponent) return;

        setSubmitting(true);
        setError(null);

        try {
            const challengeData = {
                challengerId: playerId,
                challengerName: playerName,
                challengerRankAtTime: myRank.rank,
                defenderId: opponent.playerRosterId,
                defenderName: opponent.gamerTag || opponent.playerName,
                defenderRankAtTime: opponent.rank,
                status: 'pending',
                challengeWindow: new Date().toISOString().slice(0, 10),
                isPreseasonMatch: settings?.isPreseason || false,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
            };

            await addDoc(collection(db, 'ssbuChallenges'), challengeData);
            setSubmitted(true);
        } catch (err) {
            console.error('Error submitting challenge:', err);
            setError('Failed to submit challenge. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        setSubmitted(false);
        setSelectedOpponent(null);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="card max-w-lg w-full max-h-[80vh] overflow-auto">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        ⚔️ Issue a Challenge
                    </h3>
                    <button
                        onClick={handleClose}
                        className="text-secondary-400 hover:text-white text-xl"
                    >
                        ✕
                    </button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <div className="w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                ) : submitted ? (
                    <div className="text-center py-8">
                        <div className="text-4xl mb-4">✅</div>
                        <h4 className="text-xl font-bold text-white mb-2">Challenge Submitted!</h4>
                        <p className="text-secondary-400 mb-4">
                            {settings?.isPreseason
                                ? 'Your opponent must accept the challenge before you can play.'
                                : 'Coordinate with your opponent to schedule the match.'}
                        </p>
                        <p className="text-sm text-secondary-500 mb-6">
                            {settings?.isPreseason
                                ? 'Once accepted, both players report results to complete the match.'
                                : 'Report results to Coach via Discord or in-person.'}
                        </p>
                        <button onClick={handleClose} className="btn-primary">
                            Done
                        </button>
                    </div>
                ) : !settings?.challengeWindowOpen && !(settings?.isPreseason && settings?.allowOpenChallenges) ? (
                    <div className="text-center py-8">
                        <div className="text-4xl mb-4">🔒</div>
                        <h4 className="text-xl font-bold text-white mb-2">Challenge Window Closed</h4>
                        <p className="text-secondary-400">
                            Challenges can only be submitted when the window is open.
                            Check back later!
                        </p>
                    </div>
                ) : !myRank ? (
                    <div className="text-center py-8">
                        <div className="text-4xl mb-4">❓</div>
                        <h4 className="text-xl font-bold text-white mb-2">Not on Ladder</h4>
                        <p className="text-secondary-400">
                            You're not currently on the SSBU ladder.
                            Contact Coach to be added.
                        </p>
                    </div>
                ) : myRank.rank === 1 ? (
                    <div className="text-center py-8">
                        <div className="text-4xl mb-4">👑</div>
                        <h4 className="text-xl font-bold text-white mb-2">You're #1!</h4>
                        <p className="text-secondary-400">
                            There's no one above you to challenge. Defend your throne!
                        </p>
                    </div>
                ) : eligibleOpponents.length === 0 ? (
                    <div className="text-center py-8">
                        <div className="text-4xl mb-4">⚠️</div>
                        <h4 className="text-xl font-bold text-white mb-2">No Eligible Opponents</h4>
                        <p className="text-secondary-400">
                            Based on your current rank (#{myRank.rank}), there are no opponents within your challenge range.
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Current rank info */}
                        <div className="bg-secondary-800/50 rounded-lg p-4 mb-4">
                            <div className="text-sm text-secondary-400 mb-1">Your Current Rank</div>
                            <div className="text-2xl font-bold text-white">
                                #{myRank.rank} — {myRank.gamerTag || myRank.playerName}
                            </div>
                            <div className="text-sm text-secondary-500 mt-1">
                                Can challenge up to {myRank.rank <= (settings?.topTierThreshold || 5) ? 1 : 3} rank{myRank.rank <= (settings?.topTierThreshold || 5) ? '' : 's'} ahead
                            </div>
                        </div>

                        {/* Opponent selection */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-secondary-300 mb-2">
                                Select Opponent to Challenge
                            </label>
                            <div className="space-y-2">
                                {eligibleOpponents.map(opponent => (
                                    <button
                                        key={opponent.id}
                                        onClick={() => setSelectedOpponent(opponent.id)}
                                        className={`w-full p-3 rounded-lg text-left transition-colors flex items-center justify-between ${selectedOpponent === opponent.id
                                            ? 'bg-red-500/20 border-2 border-red-500'
                                            : 'bg-secondary-700 hover:bg-secondary-600 border-2 border-transparent'
                                            }`}
                                    >
                                        <div>
                                            <span className="font-bold text-white">#{opponent.rank}</span>
                                            <span className="ml-2 text-secondary-300">
                                                {opponent.gamerTag || opponent.playerName}
                                            </span>
                                        </div>
                                        {selectedOpponent === opponent.id && (
                                            <span className="text-red-400">⚔️</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {error && (
                            <div className="text-red-400 text-sm mb-4 p-3 bg-red-500/10 rounded-lg">
                                {error}
                            </div>
                        )}

                        {/* Submit button */}
                        <button
                            onClick={handleSubmitChallenge}
                            disabled={!selectedOpponent || submitting}
                            className="btn-primary w-full disabled:opacity-50"
                        >
                            {submitting ? 'Submitting...' : 'Submit Challenge'}
                        </button>

                        <p className="text-xs text-secondary-500 text-center mt-3">
                            After submitting, coordinate with your opponent to schedule the match.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
